import { ClickEvent, DataSourceMode, DataSourceModeChangeEvent, Textbox } from "@mcleod/components";
import { AutogenLayoutMobileCommControl } from "./autogen/AutogenLayoutMobileCommControl";
import { VendorStrategyFactory } from "./strategies/VendorStrategy";
import { DuplicateId } from "../DuplicateId";
import { CommonDialogs } from "@mcleod/common";
import { MobileCommValidationUtils, VALIDATION_CONFIG } from "./MobileCommValidationUtils";

export class MobileCommControl extends AutogenLayoutMobileCommControl {
    /**
     * Initialize the control when it loads
     */
    async onLoad() {
        try {
            await super.onLoad();
            this.setupEventListeners();
            this.setupValidationRules();


            this.addLayoutDataLoadListener(async event => {
                this.layoutMobileCommControlConfigurations.parentDataSource = this.mainDataSource;
            });
        } catch (error) {
            console.error("Error loading MobileCommControl:", error);
        }
    }


    private async setupValidationRules(): Promise<void> {
        this.mainDataSource.doBeforeDataSourcePost = async () => {

            const isValidCode = await DuplicateId.validateBeforePost(this.layoutMobileCommControlGeneral.textboxVendorId, VALIDATION_CONFIG.DUPLICATE_VALIDATION_PROPS);
            if (!isValidCode) {
                return false;
            }

            const vendorId = this.layoutMobileCommControlGeneral.textboxVendorId.text?.trim();

            if (vendorId && vendorId.length >= VALIDATION_CONFIG.VENDOR_ID_MIN_LENGTH) {
                const isTrackingEnabled = this.layoutMobileCommControlGeneral.switchTrailerTracking.checked ||
                                         this.layoutMobileCommControlGeneral.switchReeferTracking.checked;

                const isValid = await MobileCommValidationUtils.validateVendorId(
                    this.layoutMobileCommControlGeneral.textboxVendorId,
                    vendorId,
                    isTrackingEnabled,
                    (vendorId) => this.layoutMobileCommControlGeneral.updateSendLoadListenerTableVisibility(vendorId)
                );

                if (!isValid) {
                    const response = MobileCommValidationUtils.createVendorValidationMessage(vendorId, isTrackingEnabled);
                    CommonDialogs.showDialog(response, VALIDATION_CONFIG.DIALOG_PROPS).finally(() => {
                        this.layoutMobileCommControlGeneral.textboxVendorId.enabledDuringAdd = true;
                        this.layoutMobileCommControlGeneral.textboxVendorId.focus();
                    });
                }
                return isValid;
            }

            return true;
        };
    }

    /**
     * Set up event listeners for the form
     */
    private setupEventListeners(): void {
        // Set up data source listeners
        if (this.sourceMCControl) {
            this.sourceMCControl.addAfterModeChangeListener(this.onModeChange.bind(this));
        }
    }

    /**
     * Handle data source mode changes
     */
    private onModeChange(event: DataSourceModeChangeEvent): void {
        const mode = event.newMode;

        switch (mode) {
            case DataSourceMode.ADD:
                this.onAddMode();
                break;
            case DataSourceMode.UPDATE:
                this.onUpdateMode();
                break;
            case DataSourceMode.SEARCH:
                this.onSearchMode();
                break;
        }
    }

    /**
     * Handle add mode
     */
    private onAddMode() {
        // Hide conditional sections initially
        this.tabSendLoad.visible = false || this.isSymphonyVendorType();
        this.tabSendLoadListener.visible = false || this.isSymphonyVendorType();
        this.tabSendLoadOptions.visible = false || this.isSymphonyVendorType();
        this.tabTrailer.visible = false;
    }

    /**
     * Handle update mode
     */
    private onUpdateMode(): void {
        this.tabSendLoadOptions.visible = this.showSendLoadOptonsTab();
        this.tabSendLoadListener.visible = this.showSendLoadListenerTab();
    }

    protected showSendLoadOptonsTab(): boolean {
        const mcControlId = this.mainDataSource?.activeRow?.get("id", "");
        if (mcControlId) {
            const strategy = VendorStrategyFactory.getStrategy(mcControlId);
            return strategy != null || this.isSymphonyVendorType();
        }
        return false;
    }

    protected showSendLoadListenerTab(): boolean {
        const mcControlId = this.mainDataSource?.activeRow?.get("id", "");
        if (mcControlId) {
            return this.isSendLoadListenerVendorType();
        }
    }

    /**
     * Handle search mode
     */
    private onSearchMode(): void {
        // Show all sections for search
        this.tabSendLoad.visible = true;
        this.tabSendLoadListener.visible = true;
        this.tabSendLoadOptions.visible = true;
        this.tabTrailer.visible = true;
    }

    /**
     * Handle upload config files button click
     */
    async buttonUploadConfigFilesOnClick(event: ClickEvent): Promise<void> {
        try {
            // Implement file upload logic
            console.log("Upload config files button clicked");
            // This would typically open a file dialog or upload interface
        } catch (error) {
            console.error("Error uploading config files:", error);
        }
    }

    /**
     * Check if the active row's ID matches supported vendor types
     * @returns true if the ID is KEEPTRUCK, SAMSARA, or SYMPHONY; false otherwise
     */
    isSendLoadListenerVendorType(): boolean {
        try {
            const vendorId = this.mainDataSource?.activeRow?.get("id", "");
            return vendorId === "KEEPTRUCK" || vendorId === "SAMSARA" || vendorId === "SYMPHONY";
        } catch (error) {
            return false;
        }
    }

    isSymphonyVendorType(): boolean {
        try {
            const vendorId = this.mainDataSource?.activeRow?.get("id", "");
            return vendorId.startsWith("SYM");
        } catch (error) {
            return false;
        }
    }

    async postDataSources(): Promise<boolean> {
        try {
            await this.mainDataSource.post();
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Handle actions after data source execution
     * Controls visibility of UI components based on mode and vendor ID
     */
    sourceMCControlAfterExecution(event): void {
        this.tabContacts.visible = this.isSymphonyVendorType();
    }
}
